/*
 * Collection image mode ("Product" vs "On Model")
 * ---------------------------------------------------------------------------
 * <collection-image-mode> wraps the collection's switcher + product grid.
 * - The switcher buttons ([data-view-mode="flat|model"]) flip the mode.
 * - In "model" mode, cards that have a model image (.product-card--has-model)
 *   show their on-model layer instead of the standard image.
 * - The on-model <img> ships with a transparent placeholder; its real src is
 *   set lazily the first time model mode is entered (per selected variant, from
 *   the per-card variant->image map, falling back to data-model-fallback for
 *   single-variant products). Selecting a swatch updates it while in model mode.
 *   The chosen mode is remembered via localStorage.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'redspec:collection-image-mode';

  class CollectionImageMode extends HTMLElement {
    connectedCallback() {
      // Nothing to switch if no product on this page has a model image — hide the bar.
      if (this.querySelectorAll('.product-card--has-model').length === 0) {
        var emptySwitcher = this.querySelector('.collection-view-switcher');
        if (emptySwitcher) emptySwitcher.hidden = true;
        return;
      }

      var stored = null;
      try { stored = localStorage.getItem(STORAGE_KEY); } catch (e) {}
      var mode = stored === 'model' || stored === 'flat' ? stored : (this.getAttribute('data-image-mode') || 'flat');

      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);

      this.setMode(mode, false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
    }

    _onClick(event) {
      var button = event.target.closest('[data-view-mode]');
      if (!button || !this.contains(button)) return;
      this.setMode(button.getAttribute('data-view-mode'), true);
    }

    _onChange(event) {
      // Only react to swatch changes while showing the model view — keeps the
      // standard view from ever fetching a model image.
      if (this.getAttribute('data-image-mode') !== 'model') return;
      var control = event.target.closest('[data-variant-id]');
      if (!control) return;
      var card = control.closest('.product-card--has-model');
      if (card) this.setCardModelImage(card, control);
    }

    setMode(mode, persist) {
      if (mode !== 'model') mode = 'flat';
      this.setAttribute('data-image-mode', mode);

      var buttons = this.querySelectorAll('[data-view-mode]');
      for (var i = 0; i < buttons.length; i++) {
        var active = buttons[i].getAttribute('data-view-mode') === mode;
        buttons[i].classList.toggle('is-active', active);
        buttons[i].setAttribute('aria-pressed', active ? 'true' : 'false');
      }

      if (persist) {
        try { localStorage.setItem(STORAGE_KEY, mode); } catch (e) {}
      }

      // Hydrate/refresh every model card the first time (and each time) model mode is on.
      if (mode === 'model') {
        var cards = this.querySelectorAll('.product-card--has-model');
        for (var c = 0; c < cards.length; c++) this.syncCard(cards[c]);
      }
    }

    syncCard(card) {
      // Use the selected swatch if there is one; otherwise fall back (single-variant products).
      var control = card.querySelector('[data-variant-id]:checked') || card.querySelector('[data-variant-id]');
      this.setCardModelImage(card, control);
    }

    setCardModelImage(card, control) {
      var image = card.querySelector('.product-card__image--model');
      if (!image) return;

      var url = null;
      if (control) {
        var variantId = control.getAttribute('data-variant-id');
        var dataEl = card.querySelector('.product-card__model-data');
        if (dataEl && variantId) {
          try {
            var map = JSON.parse(dataEl.textContent);
            if (map && map[variantId]) url = map[variantId];
          } catch (e) {}
        }
      }

      if (!url) url = card.getAttribute('data-model-fallback');
      if (!url) return;

      if (image.getAttribute('src') !== url) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = url;
      }
    }
  }

  if (!customElements.get('collection-image-mode')) {
    customElements.define('collection-image-mode', CollectionImageMode);
  }
})();
